import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import {
  ShieldCheck, CheckCircle2, ArrowRight, Clock, FileText, Download,
  UserCheck, Info, Sparkles, Percent, PiggyBank, Smartphone,
  ChevronDown, ChevronUp, Users, GraduationCap, Award, HelpCircle, PhoneCall, X
} from 'lucide-react';

export default function SavingsAccount() {
  const [activeTier, setActiveTier] = useState('regular');
  const [calcBalance, setCalcBalance] = useState(50000);
  const [faqOpen, setFaqOpen] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: '',
    accountType: 'Savings Account'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const entryId = `acc_${Date.now()}`;
    const payload = {
      id: entryId,
      name: formData.name,
      contact: formData.contact,
      email: formData.email,
      city: formData.city,
      accountType: formData.accountType,
      status: 'Open',
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(db, 'account_enquiries', entryId), payload);
    } catch (error) {
      console.warn("Could not save account enquiry online:", error);
    }
    alert(`Thank you, ${formData.name}! Your application for a ${formData.accountType} has been received. Our executive will contact you at ${formData.contact} within 24 hours.`);
    setIsModalOpen(false);
    setFormData({
      name: '',
      contact: '',
      email: '',
      city: '',
      accountType: 'Savings Account'
    });
  };

  const accountTiers = {
    regular: {
      name: 'Regular Savings Account',
      tagline: 'Foundational liquid account for everyday deposits',
      minBalance: '₹1,000 (Urban) / ₹500 (Rural)',
      target: 'Individuals, Retailers, Households',
      features: [
        'Quarterly interest payouts credited directly to the ledger.',
        'Access to physical passbook facility at all branches.',
        'Seamless integration with standard UPI payment applications.',
        'Nomination facility available upon account creation.'
      ],
      icon: PiggyBank
    },
    premium: {
      name: 'Premium Savings Account',
      tagline: 'Higher yield tier with premium transaction facilities',
      minBalance: '₹10,000 (Average Quarterly)',
      target: 'High-net-worth individuals and professionals',
      features: [
        'Higher priority service desks at all Sahyadri branches.',
        'Complementary first-year platinum debit card.',
        'Free monthly email statements and personalized cheque book.',
        'Accident insurance cover up to ₹2 Lakhs (linked to debit card).'
      ],
      icon: Award
    },
    salary: {
      name: 'Salary Savings Account',
      tagline: 'Zero-balance accounts for salaried professionals',
      minBalance: 'Zero Balance (Subject to monthly salary credit)',
      target: 'Corporate, government, and private firm employees',
      features: [
        'Zero minimum balance maintenance rules.',
        'Automated bill payment configurations (auto-debit).',
        'Preferential rates on personal and vehicle loans.',
        'Free multi-city cheque books and overdraft options up to 1 month salary.'
      ],
      icon: Clock
    },
    senior: {
      name: 'Senior Citizen Savings Account',
      tagline: 'Dedicated accounts for elders with premium comfort',
      minBalance: '₹500 (All branches)',
      target: 'Members aged 60 years and above',
      features: [
        'Special assistance desks at physical branches.',
        'Free door-step banking assistance (selected areas).',
        'Direct integration with pension credit systems.',
        'Form 15H compliance assistance to avoid direct TDS deduction.'
      ],
      icon: Users
    },
    women: {
      name: "Women's Savings Account",
      tagline: 'Empowering women with micro-savings incentives',
      minBalance: '₹500',
      target: 'Women applicants (Sole or Primary joint holders)',
      features: [
        'Higher savings interest rate incentives of 4.25% p.a.',
        'Zero balance maintenance rules if linked to a recurring deposit.',
        'Special micro-finance and gold loan rate discounts.',
        'Complimentary financial literacy workshops participation.'
      ],
      icon: Sparkles
    },
    minor: {
      name: 'Minor Savings Account',
      tagline: 'Instill savings habits in young children early',
      minBalance: '₹200',
      target: 'Children under 18 (Operated by Parent/Guardian)',
      features: [
        'No maintenance penalties for low balances.',
        'Custom photo passbook for minors to encourage savings pride.',
        'Automated recurring deposit transfers from guardian ledger.',
        'Transition to regular savings account automatically at age 18.'
      ],
      icon: ShieldCheck
    },
    student: {
      name: 'Student Savings Account',
      tagline: 'Zero-fee banking for academic builders',
      minBalance: 'Zero Balance',
      target: 'Students aged 15-25 with valid college identity',
      features: [
        'Complete zero-balance operations.',
        'Free mobile banking application and digital passbook.',
        'Free demand drafts for educational institute payments.',
        'Special vouchers and rewards for top academic performers.'
      ],
      icon: GraduationCap
    },
    digital: {
      name: 'Digital Savings Account',
      tagline: 'Fully digital onboarding with smart tracking tools',
      minBalance: '₹1,000 (With digital e-statement enrollment)',
      target: 'Tech-savvy youngsters and online traders',
      features: [
        'Paperless online registration options.',
        'Real-time transaction alerts via push notifications.',
        'Access to advanced online security keys and card freezing tools.',
        'Free SMS and email alerts with zero service fees.'
      ],
      icon: Smartphone
    }
  };

  const interestSlabs = [
    { slab: 'Up to ₹1 Lakh', rate: '3.50% p.a.', yield: 'Liquid quarterly credit' },
    { slab: '₹1 Lakh to ₹10 Lakhs', rate: '4.00% p.a.', yield: 'Optimized savings returns' },
    { slab: 'Above ₹10 Lakhs', rate: 'As per Bank Policy', yield: 'Subject to management approval' }
  ];

  const serviceCharges = [
    { service: 'Account Opening Form', charge: 'Nil' },
    { service: 'Debit Card Issuance', charge: 'Nil (First year free for Premium & Salary)' },
    { service: 'Cheque Book (25 leaves/year)', charge: 'Free (Additional leaves chargeable)' },
    { service: 'Passbook Issuance & Printing', charge: 'Free' },
    { service: 'Duplicate Passbook', charge: '₹100 (In case of loss/damage)' },
    { service: 'ATM Cash Withdrawal Limit', charge: 'Free at Sahyadri ATMs; Standard charges elsewhere' },
    { service: 'Stop Payment Instruction', charge: '₹50 per request' }
  ];

  const faqs = [
    {
      q: 'Who is eligible to open a Savings Account with Sahyadri?',
      a: 'Any resident Indian individual, joint applicants, senior citizens, students, and guardians on behalf of minors can open a Savings Account. The applicant must fulfill standard KYC regulations.'
    },
    {
      q: 'What is the minimum balance requirement for a Savings Account?',
      a: 'The minimum balance requirement varies by branch location. For Urban branches, it is ₹2,000; for Semi-Urban branches, it is ₹1,000; and for Rural branches, it is ₹500. Special categories like Students and Salary accounts enjoy Zero-Balance facilities.'
    },
    {
      q: 'How is the interest calculated and when is it credited?',
      a: 'Interest is calculated daily based on your end-of-day balances and is credited directly to your savings account quarterly (at the end of March, June, September, and December).'
    },
    {
      q: 'Can I link my Sahyadri Savings Account to UPI applications?',
      a: 'Absolutely! Our savings account supports seamless UPI integration. You can register your account on standard apps like Google Pay, PhonePe, Paytm, or BHIM using your active debit card and mobile number.'
    },
    {
      q: 'What KYC documents are mandatory for opening a savings account?',
      a: 'You need to submit your Aadhaar Card, PAN Card, two recent passport-size photographs, and a valid address proof (like a recent utility bill) along with the completed account opening form.'
    }
  ];

  const toggleFaq = (index) => {
    setFaqOpen(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const calculateInterest = () => {
    const balance = parseFloat(calcBalance) || 0;
    let rate = 3.50;
    if (balance > 100000) {
      rate = 4.00;
    }
    const annualInterest = (balance * rate) / 100;
    const quarterlyInterest = annualInterest / 4;
    const monthlyInterest = annualInterest / 12;

    return {
      rate: rate.toFixed(2),
      annual: Math.round(annualInterest).toLocaleString('en-IN'),
      quarterly: Math.round(quarterlyInterest).toLocaleString('en-IN'),
      monthly: Math.round(monthlyInterest).toLocaleString('en-IN')
    };
  };

  const calculationResults = calculateInterest();

  return (
    <div className="pt-[68px] bg-neutralBg font-sans text-gray-700 min-h-screen">
      {/* 1. HERO HEADER */}
      <section 
        className="relative overflow-hidden text-white py-24 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "linear-gradient(rgba(5, 32, 70, 0.9), rgba(5, 32, 70, 0.95)), url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(179,24,37,0.15),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-6 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-white/10 px-4 py-1.5 rounded-full inline-block backdrop-blur-sm border border-white/10">
            Smart Savings Ledgers
          </span>
          <h1 className="font-montserrat font-extrabold text-4xl sm:text-5xl md:text-6xl text-white tracking-tight leading-tight">
            Tailored Savings Accounts
          </h1>
          <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Save smarter with cooperative security, attractive interest slabs, and seamless digital access. Avail flexible withdrawals and personalized packages built for your family.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg transform hover:-translate-y-0.5"
            >
              Apply for Account
            </button>
            <a href="#calculator" className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/20 backdrop-blur-sm">
              Calculate Interest
            </a>
          </div>
        </div>
      </section>

      {/* 2. KEY HIGHLIGHTS CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-[#B31825]/10 text-[#B31825] rounded-xl">
              <Percent className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Interest Rate</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">Up to 4.00% p.a.</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Calculated daily, paid quarterly</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-[#052046]/10 text-[#052046] rounded-xl">
              <PiggyBank className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Minimum Balance</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">Starts at ₹500</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Zero balance for students/salary</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. DYNAMIC ACCOUNT TIERS SECTION */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full">
            Choose Your Tier
          </span>
          <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#052046]">
            Savings Accounts Tailored for Everyone
          </h2>
          <p className="text-gray-500 text-sm sm:text-base font-light">
            We understand that financial goals differ. Explore our range of specialized savings ledgers designed to match your specific lifestyle.
          </p>
        </div>

        {/* Tier Tabs Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Tabs panel */}
          <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-1 gap-3">
            {Object.keys(accountTiers).map((key) => {
              const TierIcon = accountTiers[key].icon;
              const isActive = activeTier === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTier(key)}
                  className={`flex items-center space-x-3 p-4 rounded-xl text-left border transition-all ${
                    isActive
                      ? 'bg-[#052046] text-white border-[#052046] shadow-md transform translate-x-1'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <TierIcon className={`h-5 w-5 shrink-0 ${isActive ? 'text-white' : 'text-[#B31825]'}`} />
                  <span className="text-xs sm:text-sm font-semibold truncate">{accountTiers[key].name}</span>
                </button>
              );
            })}
          </div>

          {/* Details Card */}
          <div className="lg:col-span-8 bg-white border border-gray-200 rounded-3xl p-8 shadow-md relative overflow-hidden min-h-[380px] flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#B31825]/5 rounded-bl-full pointer-events-none" />
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="font-montserrat font-extrabold text-2xl text-[#052046]">
                  {accountTiers[activeTier].name}
                </h3>
                <p className="text-sm text-[#B31825] font-medium">{accountTiers[activeTier].tagline}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100 text-sm">
                <div>
                  <span className="text-xs text-gray-400 font-bold block uppercase">Minimum Balance Requirement</span>
                  <span className="font-semibold text-gray-800">{accountTiers[activeTier].minBalance}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-bold block uppercase">Target Audience</span>
                  <span className="font-semibold text-gray-800">{accountTiers[activeTier].target}</span>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-gray-400">Key Features & Privileges</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm">
                  {accountTiers[activeTier].features.map((feat, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-[#00A86B] shrink-0 mt-0.5" />
                      <span className="text-gray-600 font-light leading-snug">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4 mt-6">
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <ShieldCheck className="h-4 w-4 text-[#B31825]" />
                <span>KYC compliance is mandatory</span>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center space-x-2 px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow"
              >
                <span>Select & Enquire</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. INTEREST SLABS & CHARGES SECTION */}
      <section className="bg-white py-20 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Slab Table */}
          <div className="lg:col-span-6 space-y-6">
            <div className="space-y-2">
              <h3 className="font-montserrat font-extrabold text-2xl text-[#052046]">Savings Interest Yields</h3>
              <p className="text-gray-500 text-sm font-light">Earn competitive quarterly returns on your daily liquid balance.</p>
            </div>
            <div className="overflow-hidden border border-gray-200 rounded-2xl shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[#052046]">Balance Slab</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[#052046]">Interest Rate (p.a.)</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[#052046]">Yield Method</th>
                  </tr>
                </thead>
                <tbody className="text-xs sm:text-sm divide-y divide-gray-100">
                  {interestSlabs.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50">
                      <td className="p-4 font-semibold text-gray-800">{row.slab}</td>
                      <td className="p-4 text-[#B31825] font-bold">{row.rate}</td>
                      <td className="p-4 text-gray-500 font-light">{row.yield}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <Info className="h-4 w-4 text-[#052046]" />
              <span>Interest rates are subject to revision as per cooperative directives.</span>
            </div>
          </div>

          {/* Charges Table */}
          <div className="lg:col-span-6 space-y-6">
            <div className="space-y-2">
              <h3 className="font-montserrat font-extrabold text-2xl text-[#052046]">Service Charges & Fees</h3>
              <p className="text-gray-500 text-sm font-light">Transparent, customer-friendly pricing with no surprise fees.</p>
            </div>
            <div className="overflow-hidden border border-gray-200 rounded-2xl shadow-sm max-h-[290px] overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[#052046]">Banking Service</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-[#052046]">Charges Applicable</th>
                  </tr>
                </thead>
                <tbody className="text-xs sm:text-sm divide-y divide-gray-100">
                  {serviceCharges.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50">
                      <td className="p-4 text-gray-800 font-medium">{row.service}</td>
                      <td className="p-4 text-gray-600 font-light">{row.charge}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <Info className="h-4 w-4 text-[#B31825]" />
              <span>Monthly e-statements are delivered completely free of charge.</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. INTERACTIVE INTEREST CALCULATOR */}
      <section id="calculator" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#052046] text-white rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#B31825]/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
            {/* Input Side */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-3">
                <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-white/10 px-3 py-1 rounded-full border border-white/5 inline-block">
                  Savings Growth Simulator
                </span>
                <h3 className="font-montserrat font-extrabold text-3xl text-white">Savings Interest Calculator</h3>
                <p className="text-gray-300 text-sm font-light">
                  Simulate your interest yield based on average monthly balances. Drag the slider to adjust your budget.
                </p>
              </div>

              {/* Slider Balance */}
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span>Average Balance Maintained</span>
                  <span className="text-lg text-white font-mono bg-white/10 px-3 py-1 rounded-lg">
                    ₹{parseInt(calcBalance).toLocaleString('en-IN')}
                  </span>
                </div>
                <input
                  type="range"
                  min="2000"
                  max="1500000"
                  step="5000"
                  value={calcBalance}
                  onChange={(e) => setCalcBalance(e.target.value)}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#B31825]"
                />
                <div className="flex justify-between text-xs text-gray-400 font-mono">
                  <span>₹2,000</span>
                  <span>₹5,00,000</span>
                  <span>₹10,00,000</span>
                  <span>₹15,00,000</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 text-xs font-light text-gray-300 border-t border-white/10">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#00A86B]" />
                  <span>Compound Interest</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#00A86B]" />
                  <span>Real-time calculation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-[#00A86B]" />
                  <span>Daily interest accrual</span>
                </div>
              </div>
            </div>

            {/* Results Side */}
            <div className="lg:col-span-5 bg-white text-navy rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
              <div className="text-center pb-4 border-b border-gray-100">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Estimated Annual Rate</span>
                <span className="text-3xl font-extrabold text-[#B31825] font-mono mt-1 block">
                  {calculationResults.rate}% <span className="text-sm font-medium">p.a.</span>
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100 text-sm">
                  <span className="text-gray-500 font-light">Monthly Accrued Interest</span>
                  <span className="font-bold text-gray-800 font-mono">₹{calculationResults.monthly}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100 text-sm">
                  <span className="text-gray-500 font-light">Quarterly Credit Payout</span>
                  <span className="font-bold text-gray-800 font-mono">₹{calculationResults.quarterly}</span>
                </div>
                <div className="flex justify-between items-center py-2 text-sm font-semibold">
                  <span className="text-[#052046]">Estimated Annual Yield</span>
                  <span className="text-lg text-[#00A86B] font-mono">₹{calculationResults.annual}</span>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow block text-center font-sans"
              >
                Apply for Account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 6. ELIGIBILITY & MANDATORY DOCUMENTS */}
      <section className="bg-white py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">Eligibility & KYC Requirements</h3>
            <p className="text-gray-500 text-sm font-light mt-2">Get your papers ready for a fast, frictionless verification process at our branches.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Eligibility */}
            <div className="bg-[#F7F9FC] border border-gray-200 p-8 rounded-3xl space-y-6">
              <h4 className="font-montserrat font-extrabold text-lg text-[#052046] flex items-center">
                <UserCheck className="h-5 w-5 mr-3 text-[#B31825]" />
                <span>Eligible Candidates</span>
              </h4>
              <p className="text-xs text-gray-400 font-light">The following categories of members can initiate a Savings ledger account:</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm text-gray-600 font-light">
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Resident Indian Individuals</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Joint Account Holders</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Senior Citizen Applicants</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Students and Minors</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Salaried & Self-employed Professionals</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Women Entrepreneurs</li>
              </ul>
            </div>

            {/* Documents */}
            <div className="bg-[#F7F9FC] border border-gray-200 p-8 rounded-3xl space-y-6">
              <h4 className="font-montserrat font-extrabold text-lg text-[#052046] flex items-center">
                <FileText className="h-5 w-5 mr-3 text-[#B31825]" />
                <span>Documents Checklist</span>
              </h4>
              <p className="text-xs text-gray-400 font-light">Ensure you have photocopies and original credentials for in-person branch verification:</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm text-gray-600 font-light">
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Aadhaar Card (UID)</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Permanent Account Number (PAN) Card</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Address Proof (Utility Bills/Voter Card)</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Identity Proof (Passport/Driving License)</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>2 passport size recent photographs</li>
                <li className="flex items-start"><span className="text-[#B31825] mr-2 font-bold">•</span>Active Mobile Number & Valid Email ID</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. ACCOUNT OPENING PROCESS TIMELINE */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">6-Step Account Opening Process</h3>
          <p className="text-gray-500 text-sm font-light mt-2">Opening an account with Sahyadri Co-operative is simple and fully transparent.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 relative">
          {[
            { step: '01', title: 'Fill Application', desc: 'Complete the paper application or apply via website query.' },
            { step: '02', title: 'Gather KYC', desc: 'Keep Aadhaar, PAN, and utilities proofs ready.' },
            { step: '03', title: 'Complete Verification', desc: 'Visit our branch desk for physical biometric verification.' },
            { step: '04', title: 'Initial Deposit', desc: 'Add opening deposit (Starts at ₹500 depending on branch).' },
            { step: '05', title: 'Activation', desc: 'Secure ledger accounts will be approved within 24 hours.' },
            { step: '06', title: 'Receive Welcome Kit', desc: 'Receive your brand new passbook, cheque book, and ATM card.' }
          ].map((item, idx) => (
            <div key={idx} className="bg-white border border-gray-200 p-5 rounded-2xl shadow-sm text-center relative hover:border-[#B31825] transition-all group">
              <span className="text-3xl font-extrabold text-gray-200 group-hover:text-[#B31825]/20 font-mono transition-colors block">
                {item.step}
              </span>
              <h5 className="font-montserrat font-extrabold text-xs text-[#052046] mt-3 uppercase tracking-wider">
                {item.title}
              </h5>
              <p className="text-gray-500 text-[11px] font-light mt-2 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 8. DOWNLOADS CENTER */}
      <section className="bg-[#052046] text-white py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-montserrat font-extrabold text-3xl text-white">Document & Form Downloads</h3>
            <p className="text-gray-300 text-sm font-light mt-2">Get quick access to banking forms to fill out offline before visiting the branch.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Account Opening Form', size: '1.2 MB', link: '#', desc: 'Main onboarding form for individuals & joint ledgers.' },
              { title: 'KYC Upgrade Form', size: '640 KB', link: '#', desc: 'For updating addresses, signatures, or contact parameters.' },
              { title: 'Nomination (Form DA1)', size: '320 KB', link: '#', desc: 'Form to claim nominee credentials for liquid deposits.' },
              { title: 'Service Charges Booklet', size: '1.8 MB', link: '#', desc: 'Complete breakdown of transaction fees & limit lists.' }
            ].map((doc, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col justify-between hover:bg-white/10 transition-all">
                <div className="space-y-3">
                  <div className="p-2.5 bg-white/10 rounded-xl w-fit text-[#B31825]">
                    <Download className="h-5 w-5" />
                  </div>
                  <h4 className="font-montserrat font-bold text-sm text-white">{doc.title}</h4>
                  <p className="text-gray-300 text-xs font-light leading-relaxed">{doc.desc}</p>
                </div>
                <div className="pt-6 flex justify-between items-center text-xs">
                  <span className="text-gray-400 font-mono">{doc.size}</span>
                  <a href={doc.link} className="text-[#B31825] hover:underline font-bold tracking-wider uppercase flex items-center">
                    <span>PDF Form</span>
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQs ACCORDION */}
      <section className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-2">
          <HelpCircle className="h-8 w-8 text-[#B31825] mx-auto" />
          <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">Savings FAQs</h3>
          <p className="text-gray-500 text-sm font-light">Common questions about Sahyadri Savings Accounts</p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = !!faqOpen[idx];
            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex justify-between items-center p-5 text-left focus:outline-none"
                >
                  <span className="font-montserrat font-extrabold text-sm sm:text-base text-[#052046]">
                    {faq.q}
                  </span>
                  {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#B31825] shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 shrink-0 ml-4" />
                  )}
                </button>

                <div className={`transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="p-5 pt-0 border-t border-gray-100 text-xs sm:text-sm text-gray-500 leading-relaxed font-light">
                    {faq.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 10. CALL TO ACTION PANEL */}
      <section className="bg-white border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#B31825] to-[#8a0f19] text-white rounded-3xl p-8 sm:p-12 shadow-xl flex flex-col lg:flex-row justify-between items-center gap-8">
            <div className="space-y-3 text-center lg:text-left">
              <h3 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-white">Start Earning 4.00% p.a. Today</h3>
              <p className="text-white/80 text-sm max-w-xl font-light">
                Drop your query or call us at our central helpdesk to set up an onboarding agent visit.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center shrink-0">
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-3.5 bg-white text-[#B31825] hover:bg-gray-100 font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md"
              >
                Apply for Account
              </button>
              <a href="tel:18001234567" className="px-8 py-3.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center space-x-2">
                <PhoneCall className="h-4 w-4" />
                <span>Call Helpline</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 11. APPLICATION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 relative animate-fadeIn">
            
            {/* Modal Header */}
            <div className="bg-[#052046] text-white p-6 relative">
              <h3 className="font-montserrat font-extrabold text-lg text-white">Apply for Sahyadri Account</h3>
              <p className="text-xs text-gray-300 mt-1 font-light">Fill out your contact details to start onboarding.</p>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Contact Number</label>
                <input
                  type="tel"
                  name="contact"
                  required
                  placeholder="Enter 10-digit mobile"
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">City / Location</label>
                <input
                  type="text"
                  name="city"
                  required
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Account Type</label>
                <select
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                >
                  <option value="Savings Account">Savings Account</option>
                  <option value="Current Account">Current Account</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
